import type { ReactNode } from 'react';
import { ChevronIcon } from './icons';

/**
 * `.row-list` — rows grouped in a card with hairline separators: the pattern behind «Мои авто»,
 * «Способы оплаты», the profile menu and the home screen's quick links (docs/22 §3.1).
 *
 * Metrics from the prototype: `surface`, radius 16, a 1px `line` border AND the card shadow —
 * both, not one; rows are 13px vertical / 16px horizontal; the leading glyph sits in its own
 * 40×40 tile on `surface-alt` rather than floating bare.
 */
export function RowList({ children }: { children: ReactNode }) {
  return (
    <div
      className="divide-y divide-line overflow-hidden rounded-md border border-line bg-surface"
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      {children}
    </div>
  );
}

interface RowProps {
  icon?: ReactNode;
  title: string;
  subtitle?: string | undefined;
  /** Replaces the chevron — a pill, a value, a second control. */
  trailing?: ReactNode;
  onClick?: () => void;
  /**
   * A chevron means «this leads somewhere». Rows that act immediately — «Выйти», «Удалить
   * аккаунт» — must turn it off, otherwise they promise a screen that never opens.
   */
  showsChevron?: boolean;
  /** «Запросить сведения об обработке моих данных» is deliberately quieter than a menu item. */
  muted?: boolean;
  tone?: 'default' | 'danger';
  disabled?: boolean;
}

export function Row({
  icon,
  title,
  subtitle,
  trailing,
  onClick,
  showsChevron = true,
  muted = false,
  tone = 'default',
  disabled = false,
}: RowProps) {
  const titleClass = muted
    ? 'text-[13px] font-medium text-ink-soft'
    : `text-[16px] font-semibold leading-[1.3125] ${tone === 'danger' ? 'text-accent' : ''}`;

  const content = (
    <>
      {icon && (
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-sm bg-surface-alt text-ink">
          {icon}
        </span>
      )}
      <RowText title={title} subtitle={subtitle} titleClass={titleClass} />
      {trailing ??
        (onClick && showsChevron ? <ChevronIcon className="shrink-0 text-ink-soft" /> : null)}
    </>
  );

  const shared = 'flex w-full items-center gap-3 px-4 py-[13px]';

  if (!onClick) return <div className={shared}>{content}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${shared} text-left transition-colors hover:bg-surface-alt/60 active:bg-surface-alt disabled:opacity-55`}
    >
      {content}
    </button>
  );
}

/**
 * A row whose trailing control is its own button — the car list, where tapping the row edits and
 * tapping the bin deletes.
 *
 * Two sibling buttons, never one nested inside the other: HTML forbids a button inside a button,
 * and the prototype's `event.stopPropagation()` has no equivalent that survives keyboard use.
 */
export function RowWithAction({
  icon,
  title,
  subtitle,
  onClick,
  action,
}: {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  onClick: () => void;
  action: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 pr-3">
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-center gap-3 px-4 py-[13px] text-left transition-colors hover:bg-surface-alt/60 active:bg-surface-alt"
      >
        {icon && <span className="shrink-0">{icon}</span>}
        <RowText
          title={title}
          subtitle={subtitle}
          titleClass="text-[16px] font-semibold leading-[1.3125]"
        />
      </button>
      {action}
    </div>
  );
}

/**
 * Текстовая колонка строки — общая для `Row` и `RowWithAction`.
 *
 * ЗАЧЕМ ОДНА. Разметка была скопирована в обе, и обе несли одну и ту же однострочную обрезку.
 *
 * ЗАГОЛОВОК В ДВЕ СТРОКИ (`line-clamp-2`, а не `truncate`) — ЗАЧЕМ. На телефоне строке достаётся
 * 220-312 px, а правовые пункты профиля длиннее: «Оферта и политика конфиденциальности» — 328 px,
 * «Согласие на обработку персональных данных» — 368 px. Они помещались в одну строку начиная с
 * ширины колонки ~470 px, то есть не помещались никогда, и человек не видел, на какой документ
 * нажимает. Короткие пункты («Мой парк», «О сервисе») как были одной строкой, так и остались.
 *
 * Подпись — тот же кламп на две строки, и по той же причине (замер 12.09.2026 на 320 CSS-px при
 * кегле 24): она несёт не пояснение, а данные, по которым человек узнаёт свою запись, и обрезание
 * их прятало. На главной подпись строки техники — «Toyota Camry · A123AA777», в «Моём парке» —
 * «A123AA777 · АИ-95»; обе не помещались в строку, и от номера оставался огрызок. Простынёй меню
 * от этого не становится: подписи здесь короткие, вторая строка появляется только на крупном
 * кегле, а дальше второй — тот же `title` с полным текстом, что и у заголовка.
 *
 * Межстрочие заголовка задано отношением (1.3125 = те же 21px на 16px шрифта), а не в пикселях:
 * при системном увеличении шрифта жёсткая px-строка не растёт вместе с буквами, и вторая строка
 * заголовка обрезалась бы коробкой `line-clamp`.
 *
 * ЕДИНСТВЕННОЕ МЕСТО, ГДЕ «Минимальный размер шрифта» БРАУЗЕРА ВСЁ РАВНО РЕЖЕТ ТЕКСТ. Кламп
 * считает СТРОКИ, и коробка растёт вместе с ними (замер 11.09.2026 на 320 CSS-px: 42 px → 53 px),
 * но при кегле 20 px «Согласие на обработку персональных данных» просит уже четыре строки вместо
 * двух — и две последние прячутся. Отменить настройку со стороны сайта нельзя (см. src/index.css),
 * снять кламп — значит отдать строке списка пол-экрана на каждый правовой пункт. Поэтому здесь то
 * же лекарство, что у `Pill` и `Stepper`: полный текст остаётся в подсказке. Понадобится другое
 * решение — менять надо `line-clamp-2` в одном месте ниже.
 */
function RowText({
  title,
  subtitle,
  titleClass,
}: {
  title: string;
  subtitle?: string | undefined;
  titleClass: string;
}) {
  return (
    <span className="min-w-0 flex-1 text-left">
      {/* Без `block`: `line-clamp-2` сам ставит `display: -webkit-box`. */}
      <span title={title} className={`line-clamp-2 ${titleClass}`}>
        {title}
      </span>
      {subtitle && (
        // Без `block`: `line-clamp-2` сам ставит `display: -webkit-box`.
        <span title={subtitle} className="mt-0.5 line-clamp-2 text-[12px] leading-[1.3333] text-ink-soft">
          {subtitle}
        </span>
      )}
    </span>
  );
}

/**
 * `.empty-state` — an 88px circle, an 18/24/700 heading, an explanation and a way out
 * (docs/22 §3.2). Vertically centred in whatever space is left, which is why it takes `flex-1`.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-8 py-10 text-center">
      {icon && (
        <span className="mb-[18px] grid h-[88px] w-[88px] place-items-center rounded-full bg-surface-alt text-ink-soft">
          {icon}
        </span>
      )}
      <h2 className="mb-1.5 text-[18px] font-bold leading-[1.3333]">{title}</h2>
      {description && <p className="max-w-[32ch] text-ink-soft">{description}</p>}
      {action && <div className="mt-5 w-full max-w-[280px]">{action}</div>}
    </div>
  );
}
